import type {ReactElement} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {AiImageGenerationFailureState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiImageGenerationFailureStatus} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {addAiImageGenerationFailure, removeAiImageGenerationFailure, selectFailedAiImageGenerations} from '@Components/poster-editor/poster-editor-reducer';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getAiImageGenerationErrorMessage} from '@Panels/ai-image-panel/ai-image-panel-library';

export function AiImageFailedGenerationsList(): ReactElement | null {
  const dispatch = useAppDispatch();
  const failedGenerations = useAppSelector(selectFailedAiImageGenerations);

  if (failedGenerations.length === 0) {
    return null;
  }

  const onRetry = (item: AiImageGenerationFailureState): void => {
    dispatch(removeAiImageGenerationFailure(item.id));
    dispatch(addAiImageGenerationFailure({...item, status: AiImageGenerationFailureStatus.PENDING_RETRY}));
  };

  const onDismiss = (item: AiImageGenerationFailureState): void => {
    dispatch(removeAiImageGenerationFailure(item.id));
  };

  const renderRetryLink = (item: AiImageGenerationFailureState): ReactElement | null => {
    if (item.errorCode) {
      return null;
    }
    return (
      <Text
        className={`_cursor-pointer ct-error-primary`}
        val={window.i18next.t('pmwjs_retry')}
        size={TextSize.XSMALL}
        bold
        underline
        onClick={(): void => {
          onRetry(item);
        }}
      />
    );
  };

  const renderErrorMessage = (item: AiImageGenerationFailureState): ReactElement => {
    return (
      <div key={item.id} className={`flexbox flex-align-items-center gap-2 radius-8 spacing-p-2 bg-danger-10`}>
        <Icon className={'ct-error-primary'} icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} />
        <div className="flexbox flex-align-items-center flex-1 gap-1">
          <Text className={'ct-error-primary'} val={getAiImageGenerationErrorMessage(item.errorCode)} size={TextSize.XSMALL} />
          {renderRetryLink(item)}
        </div>
        <Icon
          className={'ct-neutral-primary _cursor-pointer'}
          icon={'icon-close'}
          size={IconSize.SIZE_ICON_20}
          onClick={(): void => {
            onDismiss(item);
          }}
          type={IconType.SECONDARY}
        />
      </div>
    );
  };

  return <div className={`spacing-p-t-3 flex-v-row gap-2 _full-width`}>{failedGenerations.map(renderErrorMessage)}</div>;
}
